import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Globe, Send, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_BADGE = {
  draft: ['Entwurf', 'bg-gray-100 text-gray-700'],
  submitted: ['Bei Meta eingereicht', 'bg-blue-100 text-blue-700'],
  PENDING: ['In Meta-Prüfung', 'bg-yellow-100 text-yellow-700'],
  APPROVED: ['✅ Freigegeben', 'bg-green-100 text-green-700'],
  REJECTED: ['❌ Abgelehnt', 'bg-red-100 text-red-700'],
  error: ['❌ Fehler', 'bg-red-100 text-red-700'],
};

// Mehrsprachigkeit Phase 4: Übersetzen → Prüfen → Bei Meta einreichen → Status
export const WhatsAppI18nSection = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/i18n/status`, { headers: authHeaders() });
      const json = await res.json();
      setData(json);
      if (json.job?.status === 'running') setTimeout(load, 4000);
    } catch (_) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const call = async (path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(path);
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/i18n/${path}`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) { alert('❌ ' + (json.detail || 'Fehler')); return; }
      if (path === 'submit') {
        alert(`📤 ${json.submitted} Vorlagen bei Meta eingereicht${json.errors ? `, ${json.errors} Fehler` : ''}.\n\nMeta prüft sie jetzt (meist wenige Minuten bis 24h). Status mit "Status aktualisieren" abrufen.`);
      }
      load();
    } catch (e) { alert('❌ Fehler'); }
    finally { setBusy(''); }
  };

  const job = data?.job;
  const translations = data?.translations || [];
  const counts = data?.counts || {};
  const drafts = counts.draft || 0;

  return (
    <div className="mb-8 bg-gradient-to-r from-purple-50 to-fuchsia-50 border border-purple-200 rounded-xl p-6" data-testid="wa-i18n-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Globe size={18} className="text-purple-600" /> Mehrsprachige Vorlagen (EN / FR / IT)
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            1. KI übersetzt deine freigegebenen deutschen Vorlagen → 2. Prüfen → 3. Bei Meta einreichen → 4. Nach Freigabe sendet das System automatisch in der Kundensprache.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => call('translate-all')}
            disabled={busy !== '' || job?.status === 'running'}
            data-testid="wa-i18n-translate-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:opacity-60"
          >
            {job?.status === 'running' ? <Loader2 size={14} className="animate-spin" /> : '🌍'}
            {job?.status === 'running' ? `Übersetze… ${job.processed}/${job.total}` : 'Übersetzen'}
          </button>
          <button
            onClick={() => call('submit', `${drafts} übersetzte Vorlagen bei Meta zur Freigabe einreichen?`)}
            disabled={busy !== '' || drafts === 0}
            data-testid="wa-i18n-submit-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-60"
          >
            <Send size={14} /> Bei Meta einreichen {drafts > 0 && `(${drafts})`}
          </button>
          <button
            onClick={() => call('sync')}
            disabled={busy !== ''}
            data-testid="wa-i18n-sync-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 text-sm disabled:opacity-60"
          >
            {busy === 'sync' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Status aktualisieren
          </button>
        </div>
      </div>

      {job?.status === 'error' && (
        <p className="text-xs text-red-600 mt-2">Letzter Lauf fehlgeschlagen: {job.error}</p>
      )}

      {translations.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setExpanded(!expanded)} className="text-xs text-purple-700 underline" data-testid="wa-i18n-toggle-list">
            {expanded ? 'Liste ausblenden' : `${translations.length} Übersetzungen anzeigen`}
            {' '}({Object.entries(counts).map(([k, v]) => `${v}× ${STATUS_BADGE[k]?.[0] || k}`).join(' · ')})
          </button>
          {expanded && (
            <div className="mt-3 max-h-72 overflow-y-auto bg-white rounded-lg border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="text-left text-gray-500">
                    <th className="p-2">Template</th><th>Sprache</th><th>Status</th><th>Fehler</th>
                  </tr>
                </thead>
                <tbody>
                  {translations.map((t) => {
                    const [label, cls] = STATUS_BADGE[t.status] || [t.status, 'bg-gray-100 text-gray-600'];
                    return (
                      <tr key={`${t.name}-${t.language}`} className="border-t">
                        <td className="p-2 font-mono">{t.name}</td>
                        <td className="uppercase font-semibold">{t.language}</td>
                        <td><span className={`px-1.5 py-0.5 rounded ${cls}`}>{label}</span></td>
                        <td className="text-red-500 pr-2">{(t.error || '').slice(0, 80)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
